// LoginSheet + ConfirmSheet — iter98 (LoginSheet auth cleanup).
//
// LoginSheet — returning-user login sheet. Two auth paths:
//   1. WhatsApp magic link (primary, pre-filled from `habano.lastPhone`)
//   2. Google via Emergent-managed redirect flow (no GSI script)
//
// Email login was removed from this sheet; it remains available as a *backup*
// from Profile → Security. The Emergent Google flow follows the same pattern
// used by Splash.jsx / GuestGate.jsx — redirect to auth.emergentagent.com with
// `redirect={origin}/select-role`; the SPA detects `#session_id=...` in App.js
// and runs AuthCallback to POST the session_id to /api/auth/session.
//
// ConfirmSheet — generic gold/red two-button confirmation modal used for
// Sign Out and Delete Account.
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { lsRemove } from "../lib/safeStorage";
import { formatPhoneWithFlag } from "../lib/formatPhone";
import { useWaAuthEnabled } from "../lib/waAuth";

const COUNTRY_PRIORITY = ["CU", "CA", "RU", "DE", "FR", "IT", "ES", "MX", "GB", "US", "AR"];

// Inline Google "G" logo — keeps us free of external scripts / asset files.
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.185l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

export function LoginSheet({ open, onClose }) {
  const { lang } = useI18n();
  const waAuth = useWaAuthEnabled();
  const [phone, setPhone] = useState("");
  const [overrideManual, setOverrideManual] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    try { setPhone(localStorage.getItem("habano.lastPhone") || ""); } catch { /* ignore */ }
    setOverrideManual(false);
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;
  const hasPreFill = !!phone && !overrideManual && isValidPhoneNumber(phone);
  const initials = (() => {
    if (!hasPreFill) return "?";
    try {
      const parsed = parsePhoneNumber(phone);
      return (parsed?.country || "?").slice(0, 2);
    } catch { return "?"; }
  })();

  const send = async () => {
    if (!phone || !isValidPhoneNumber(phone)) {
      toast.error(lang === "es" ? "Número inválido" : "Invalid phone number");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/auth/wa/start", { phone_e164: phone });
      if (data.rate_limited) {
        toast.error(lang === "es" ? "Demasiados intentos — espera una hora" : "Too many requests — wait an hour");
        return;
      }
      try { localStorage.setItem("habano.lastPhone", phone); } catch { /* ignore */ }
      toast.success(lang === "es" ? "Revisa WhatsApp para tu enlace" : "Check WhatsApp for your link");
      console.log("[Habano dev] login verify link:", data.mock_link);
    } catch {
      toast.error(lang === "es" ? "No se pudo enviar el enlace" : "Could not send link");
    } finally { setSending(false); }
  };

  const startGoogle = () => {
    // Emergent-managed Google Auth — redirect to auth.emergentagent.com.
    // Returns to /select-role#session_id=... which App.js intercepts and
    // hands off to AuthCallback for the /api/auth/session exchange.
    // DO NOT HARDCODE THE URL OR ADD FALLBACKS — that breaks the handshake.
    lsRemove("habano.guest");
    const redirectUrl = window.location.origin + "/select-role";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return createPortal(
    <div data-testid="login-sheet" role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 480, margin: "0 auto", background: "#171717", borderTopLeftRadius: 24, borderTopRightRadius: 24, borderTop: "1px solid rgba(232,184,75,0.3)", padding: "10px 18px 24px" }}>
        <div style={{ width: 40, height: 4, background: "#333", borderRadius: 2, margin: "0 auto 16px" }} />
        <button type="button" onClick={onClose} aria-label="Close"
          style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", color: "#B0AAA3", padding: 6 }}>
          <X className="w-5 h-5" />
        </button>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", textAlign: "center", marginBottom: waAuth ? 20 : 6 }}>
          {waAuth
            ? (lang === "es" ? "Bienvenido de vuelta" : "Welcome back")
            : (lang === "es" ? "Inicia sesión en Habano" : "Sign in to Habano")}
        </h2>
        {!waAuth && (
          <p data-testid="login-google-only-sub" style={{ textAlign: "center", marginBottom: 20, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}>
            {lang === "es" ? "Usa tu cuenta de Google para continuar" : "Use your Google account to continue"}
          </p>
        )}

        {waAuth && (hasPreFill ? (
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(232,184,75,0.15)", border: "1px solid rgba(232,184,75,0.45)", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 22, fontWeight: 700, display: "grid", placeItems: "center", margin: "0 auto 12px" }}>
              {initials}
            </div>
            <div data-testid="login-prefill-phone" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8", fontWeight: 500 }}>{formatPhoneWithFlag(phone)}</div>
          </div>
        ) : (
          <div style={{ background: "#1a1a1a", border: "1px solid rgba(232,184,75,0.25)", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
            <PhoneInput defaultCountry="CU" value={phone} onChange={(v) => setPhone(v || "")}
              international countryCallingCodeEditable={false}
              countries={[...COUNTRY_PRIORITY]} addInternationalOption={false}
              style={{ color: "#F5F1E8" }} />
          </div>
        ))}

        {waAuth && (
          <button type="button" data-testid="login-send-wa" onClick={send} disabled={sending || !isValidPhoneNumber(phone || "")}
            style={{ width: "100%", minHeight: 48, borderRadius: 10, border: "none",
              background: !isValidPhoneNumber(phone || "") ? "#3a2e15" : "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
              color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
            {lang === "es" ? "Enviar enlace de WhatsApp" : "Send WhatsApp Link"}
          </button>
        )}

        {waAuth && hasPreFill && (
          <button type="button" data-testid="login-different-number"
            onClick={() => { setOverrideManual(true); setPhone(""); }}
            style={{ width: "100%", minHeight: 44, background: "transparent", border: "none", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 13, marginBottom: 10 }}>
            {lang === "es" ? "Usar otro número" : "Use a different number"}
          </button>
        )}

        {/* Divider — only shown when WA path is also present */}
        {waAuth && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "6px 0 12px" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(126,120,109,0.25)" }} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {lang === "es" ? "o" : "or"}
            </span>
            <div style={{ flex: 1, height: 1, background: "rgba(126,120,109,0.25)" }} />
          </div>
        )}

        {/* Google — always shown. Clean white pill, Google "G" logo. */}
        <button type="button" data-testid="login-google" onClick={startGoogle}
          style={{
            width: "100%", minHeight: 48, borderRadius: 10,
            background: "#FFFFFF", border: "1px solid #DADCE0",
            color: "#3C4043", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
            cursor: "pointer",
          }}>
          <GoogleLogo />
          <span>{lang === "es" ? "Iniciar sesión con Google" : "Sign in with Google"}</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmSheet({ open, title, body, confirmLabel, cancelLabel, confirmTone = "gold", onConfirm, onClose, testidPrefix = "confirm" }) {
  if (!open) return null;
  const confirmBg = confirmTone === "danger"
    ? "#8B1A1A"
    : "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)";
  const confirmColor = confirmTone === "danger" ? "#FFFFFF" : "#0B0B0B";
  return createPortal(
    <div data-testid={`${testidPrefix}-sheet`} role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 210, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.72)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 380, background: "#171717", borderRadius: 18, border: "1px solid rgba(232,184,75,0.3)", padding: "22px 20px 18px" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8", marginBottom: 10 }}>{title}</h3>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.45, marginBottom: 18 }}>{body}</p>
        <button type="button" data-testid={`${testidPrefix}-confirm`} onClick={onConfirm}
          style={{ width: "100%", minHeight: 48, borderRadius: 10, border: "none", background: confirmBg, color: confirmColor, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
          {confirmLabel}
        </button>
        <button type="button" data-testid={`${testidPrefix}-cancel`} onClick={onClose}
          style={{ width: "100%", minHeight: 48, borderRadius: 10, background: "transparent", border: "1.5px solid rgba(232,184,75,0.5)", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}>
          {cancelLabel}
        </button>
      </div>
    </div>,
    document.body,
  );
}
